import { useReadQuery } from '@apollo/client';
import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { VisuallyHidden } from '@synoptic/ui-kit/visually-hidden/visually-hidden.js';
import { FC, ReactElement, Suspense, useState } from 'react';
import { useMatch, useSearchParams } from 'react-router';
import { UnreadNotificationsQueryQuery } from '@/__generated__/graphql.ts';
import { useAuthGuard } from '@/components/auth-guard/auth-guard-provider.tsx';
import { flags } from '@/feature-flags/flags.ts';
import { useFeatureFlags } from '@/feature-flags/use-feature-flags.ts';
import { paths } from '@/routes-utils/paths.ts';
import { useAppLoader, useAuthenticatedAppLoader } from '../loader.ts';
import { useRequestTerminalAccess } from '../sidebar/menu/menu-action/use-request-terminal-access.ts';
import { useAuthInfo } from '../use-auth-info.ts';
import { useStreamDecks } from '../use-stream-decks.ts';
import { navDot, navSubMenuMore } from './nav-item-v2.css.ts';
import {
  NavButtonItemV2,
  NavLinkItemV2,
  NavSubItemV2,
  NavSubMenuV2,
} from './nav-item-v2.tsx';
import { NavItemDefV2, NavItemIdV2, navItemsV2 } from './nav-items-v2.tsx';
import { useNavVariantV2 } from './nav-variant-v2.ts';
import { requestAccessAction } from './request-access-nav-item-v2.css.ts';
import { RequestAccessNavItemV2 } from './request-access-nav-item-v2.tsx';

const SUB_ITEMS_LIMIT = 4;

const NewsDeckDecksV2 = ({ fallback }: { fallback: ReactElement }) => {
  const def = navItemsV2.newsDeck;
  const decks = useStreamDecks();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);

  if (decks.length < 2) {
    return fallback;
  }

  const activeDeckId = searchParams.get('id') || decks[0]?.id;
  const visibleDecks = showAll ? decks : decks.slice(0, SUB_ITEMS_LIMIT);

  return (
    <>
      <NavButtonItemV2
        icon={def.icon}
        label={def.label}
        active
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      />
      {open ? (
        <NavSubMenuV2>
          {visibleDecks.map((deck) => (
            <NavSubItemV2
              key={deck.id}
              to={`${paths.newsDeck}?id=${deck.id}`}
              label={deck.name}
              active={activeDeckId === deck.id}
            />
          ))}
          {decks.length > SUB_ITEMS_LIMIT ? (
            <ButtonV2
              variant="text"
              className={navSubMenuMore}
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? 'less decks' : 'more decks'}
            </ButtonV2>
          ) : null}
        </NavSubMenuV2>
      ) : null}
    </>
  );
};

const NewsDeckNavItemV2 = () => {
  const def = navItemsV2.newsDeck;
  const enabled = useFeatureFlags(flags.newsDeck);
  const { authenticated } = useAppLoader();
  const variant = useNavVariantV2();
  const isActive = Boolean(useMatch(paths.newsDeck));

  if (!enabled) {
    return null;
  }

  const link = <NavLinkItemV2 to={def.to} icon={def.icon} label={def.label} />;

  if (!authenticated || variant !== 'expanded' || !isActive) {
    return link;
  }

  return (
    <Suspense fallback={link}>
      <NewsDeckDecksV2 fallback={link} />
    </Suspense>
  );
};

const TerminalRequestAccessV2 = () => {
  const def = navItemsV2.terminal;
  const { requestAccess, loading, alreadyRequested } =
    useRequestTerminalAccess();

  return (
    <RequestAccessNavItemV2
      icon={def.icon}
      label={def.label}
      badge={def.badge}
      description="Join the waitlist to get early access to our trading terminal."
      actions={
        <ButtonV2
          variant="primary"
          className={requestAccessAction}
          disabled={alreadyRequested || loading}
          onClick={() => requestAccess()}
        >
          {alreadyRequested ? 'Access Requested' : 'Request Access'}
        </ButtonV2>
      }
    />
  );
};

const TerminalNavItemV2 = () => {
  const def = navItemsV2.terminal;
  const { authenticated } = useAppLoader();
  const guard = useAuthGuard();
  const terminalEnabled = useFeatureFlags(flags.terminal);

  const link = (
    <NavLinkItemV2
      to={def.to}
      icon={def.icon}
      label={def.label}
      badge={def.badge}
      onClick={(event) => {
        if (!terminalEnabled && guard()) {
          event.preventDefault();
        }
      }}
    />
  );

  if (!authenticated || terminalEnabled) {
    return link;
  }

  return (
    <Suspense fallback={link}>
      <TerminalRequestAccessV2 />
    </Suspense>
  );
};

const UnreadNotificationsDotV2 = () => {
  const { unreadNotificationsQueryRef } = useAuthenticatedAppLoader();
  const {
    data: {
      notifications: { items },
    },
  } = useReadQuery<UnreadNotificationsQueryQuery>(unreadNotificationsQueryRef);

  if (items.length === 0) {
    return null;
  }

  return (
    <span className={navDot} data-testid="menu-notifications-badge">
      <VisuallyHidden>
        {items.length < 10 ? items.length : '9+'} unread
      </VisuallyHidden>
    </span>
  );
};

const NotificationsNavItemV2 = () => {
  const def = navItemsV2.notifications;
  const { authenticated } = useAppLoader();

  return (
    <NavLinkItemV2 to={def.to} icon={def.icon} label={def.label}>
      {authenticated ? (
        <Suspense fallback={null}>
          <UnreadNotificationsDotV2 />
        </Suspense>
      ) : null}
    </NavLinkItemV2>
  );
};

const EnterpriseNavItemV2 = () => {
  const def = navItemsV2.enterprise;
  const { isEnterprise } = useAuthInfo();

  if (isEnterprise) {
    return <NavLinkItemV2 to={def.to} icon={def.icon} label={def.label} />;
  }

  return (
    <RequestAccessNavItemV2
      icon={def.icon}
      label={def.label}
      description="Get in touch to learn more about our enterprise solutions."
    />
  );
};

const ChartsNavItemV2 = () => {
  const def = navItemsV2.charts;
  const { authenticated } = useAppLoader();
  const enabled = useFeatureFlags(flags.streamTimeSeries);

  if (!authenticated || !enabled) {
    return null;
  }

  return <NavLinkItemV2 to={def.to} icon={def.icon} label={def.label} />;
};

const LearningCenterNavItemV2 = () => {
  const def = navItemsV2.learningCenter;
  const enabled = useFeatureFlags(flags.newsDeck);

  if (!enabled) {
    return null;
  }

  return (
    <NavLinkItemV2
      to={def.to}
      icon={def.icon}
      label={def.label}
      badge={def.badge}
    />
  );
};

const specialNavItemsV2: Partial<Record<NavItemIdV2, FC>> = {
  newsDeck: NewsDeckNavItemV2,
  learningCenter: LearningCenterNavItemV2,
  charts: ChartsNavItemV2,
  terminal: TerminalNavItemV2,
  notifications: NotificationsNavItemV2,
  enterprise: EnterpriseNavItemV2,
};

export const NavItemByIdV2 = ({ id }: { id: NavItemIdV2 }) => {
  const Special = specialNavItemsV2[id];

  if (Special) {
    return <Special />;
  }

  const def: NavItemDefV2 = navItemsV2[id];

  if (def.to !== undefined) {
    return (
      <NavLinkItemV2
        to={def.to}
        activePrefix={def.activePrefix}
        icon={def.icon}
        label={def.label}
        badge={def.badge}
      />
    );
  }

  return (
    <NavButtonItemV2
      icon={def.icon}
      label={def.label}
      badge={def.badge}
      onClick={def.action}
    />
  );
};
